import { RoleType } from "../../../App/enums"
import { Role } from "../../../Types/RoleType"

export type TutorialPlacement = "top" | "bottom" | "left" | "right" | "auto"

type TutorialContext = {
    organizationId?: number
}

type TutorialStepDefinition = {
    step: number
    role: RoleType.USER | RoleType.ORGANIZATION
    targetSelector: string
    targetSelectors?: string[]
    revealTargetSelector?: string
    revealTargetSelectors?: string[]
    preferredPlacement?: TutorialPlacement
    gap?: number
    offset?: { x?: number; y?: number }
    spotlightPadding?: number
    fallbackRoute?: (context: TutorialContext) => string | undefined
}

const tutorialSteps: TutorialStepDefinition[] = [
    {
        step: 1,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="header-burger-rooms-toggle"]',
        targetSelectors: ['[data-tutorial-anchor="header-burger-rooms-toggle"]', '[data-tutorial-anchor="room-list"]'],
        revealTargetSelectors: ['[data-tutorial-anchor="header-burger-toggle"]'],
        preferredPlacement: "right",
        offset: { y: 8 },
        fallbackRoute: () => "/dashboard",
    },
    {
        step: 2,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="user-feed"]',
        preferredPlacement: "top",
        gap: 16,
        fallbackRoute: () => "/dashboard",
    },
    {
        step: 3,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="feed-textbox"]',
        preferredPlacement: "right",
        fallbackRoute: () => "/dashboard",
    },
    {
        step: 4,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="header-profile-menu"]',
        targetSelectors: ['[data-tutorial-anchor="header-profile-menu"]', '[data-tutorial-anchor="header-profile-menu-mobile"]'],
        preferredPlacement: "left",
        offset: { y: -6 },
    },
    {
        step: 5,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="header-chat"]',
        targetSelectors: ['[data-tutorial-anchor="header-chat"]', '[data-tutorial-anchor="header-chat-mobile"]'],
        revealTargetSelector: '[data-tutorial-anchor="header-burger-toggle"]',
        revealTargetSelectors: ['[data-tutorial-anchor="header-burger-toggle"]'],
        preferredPlacement: "left",
        offset: { y: -6 },
    },
    {
        step: 6,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="header-search"]',
        targetSelectors: ['[data-tutorial-anchor="header-search"]', '[data-tutorial-anchor="header-search-mobile"]'],
        preferredPlacement: "bottom",
        gap: 14,
        fallbackRoute: () => "/dashboard",
    },
    {
        step: 7,
        role: RoleType.USER,
        targetSelector: '[data-tutorial-anchor="organization-profile-card"]',
        targetSelectors: ['[data-tutorial-anchor="organization-profile-card"]', '[data-tutorial-anchor="header-profile-menu-mobile"]'],
        preferredPlacement: "right",
        fallbackRoute: () => "/dashboard",
    },
    {
        step: 1,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="room-list"]',
        preferredPlacement: "right",
        offset: { y: 8 },
        fallbackRoute: ({ organizationId }) => (organizationId ? `/organization-dashboard/${organizationId}` : undefined),
    },
    {
        step: 2,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="room-type"]',
        preferredPlacement: "bottom",
        gap: 14,
    },
    {
        step: 3,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="room-edit"]',
        targetSelectors: ['[data-tutorial-anchor="room-edit"]', '[data-tutorial-anchor="room-edit-mobile"]'],
        revealTargetSelector: '[data-tutorial-anchor="room-mobile-menu-toggle"]',
        revealTargetSelectors: ['[data-tutorial-anchor="room-mobile-menu-toggle"]'],
        preferredPlacement: "right",
        gap: 14,
    },
    {
        step: 4,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="room-invite"]',
        targetSelectors: ['[data-tutorial-anchor="room-invite"]', '[data-tutorial-anchor="room-invite-mobile"]'],
        revealTargetSelector: '[data-tutorial-anchor="room-mobile-menu-toggle"]',
        revealTargetSelectors: ['[data-tutorial-anchor="room-mobile-menu-toggle"]'],
        preferredPlacement: "right",
    },
    {
        step: 5,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="room-media-library"]',
        preferredPlacement: "bottom",
    },
    {
        step: 6,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="room-events"]',
        preferredPlacement: "bottom",
    },
    {
        step: 7,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="header-profile-menu"]',
        targetSelectors: ['[data-tutorial-anchor="header-profile-menu"]', '[data-tutorial-anchor="header-profile-menu-mobile"]'],
        preferredPlacement: "left",
        offset: { y: -6 },
        fallbackRoute: ({ organizationId }) => (organizationId ? `/organization-dashboard/${organizationId}` : undefined),
    },
    {
        step: 8,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="organization-profile-header"]',
        preferredPlacement: "bottom",
        fallbackRoute: ({ organizationId }) => (organizationId ? `/organization-profile/${organizationId}` : undefined),
    },
    {
        step: 9,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="organization-pinboard-cards"]',
        targetSelectors: ['[data-tutorial-anchor="organization-pinboard-first-card"]', '[data-tutorial-anchor="organization-pinboard-cards"]'],
        preferredPlacement: "left",
        offset: { y: 10 },
        fallbackRoute: ({ organizationId }) => (organizationId ? `/organization-profile/${organizationId}` : undefined),
    },
    {
        step: 10,
        role: RoleType.ORGANIZATION,
        targetSelector: '[data-tutorial-anchor="organization-admin-section"]',
        preferredPlacement: "right",
        offset: { y: 8 },
        fallbackRoute: ({ organizationId }) => (organizationId ? `/organization-profile/${organizationId}` : undefined),
    },
]

export const getTutorialStepDefinition = (
    role: RoleType.USER | RoleType.ORGANIZATION | Role,
    step?: number
) => {
    if (typeof step !== "number") {
        return undefined
    }

    return tutorialSteps.find(item => item.role === role && item.step === step)
}

const getVisibleTarget = (selectors: string | string[]) => {
    if (typeof document === "undefined") {
        return null
    }

    const selectorList = (Array.isArray(selectors) ? selectors : [selectors]).flatMap(item => item.split(",").map(entry => entry.trim()))

    const elements = selectorList
        .flatMap(selector => Array.from(document.querySelectorAll(selector) as NodeListOf<HTMLElement>))
        .filter(Boolean)

    if (!elements.length) {
        return null
    }

    const visibleElement = elements.find(element => {
        const rect = element.getBoundingClientRect()
        const style = window.getComputedStyle(element)
        return rect.width > 0 && rect.height > 0 && style.display !== "none" && style.visibility !== "hidden"
    })

    return visibleElement || null
}

export const getTutorialTargetElement = (selector?: string | string[]) => {
    if (!selector || (Array.isArray(selector) && selector.length === 0)) {
        return null
    }

    return getVisibleTarget(selector)
}

export const getTutorialStartStep = (
    role: RoleType.USER | RoleType.ORGANIZATION | Role,
    context: TutorialContext = {}
) => {
    const steps = tutorialSteps
        .filter(item => item.role === role)
        .sort((left, right) => left.step - right.step)

    const currentStep = steps.find(item => getVisibleTarget(item.targetSelectors || item.targetSelector)) || steps[0]

    return {
        step: currentStep.step,
        route: currentStep.fallbackRoute?.(context),
    }
}